/**
 * Stock allocation utilities and display components.
 */
import { parseISO } from 'date-fns'
import { AlertTriangle, Check, RotateCcw } from 'lucide-react'
import type { z } from 'zod'

import type { BatchAllocationItem } from '@snootsvet/shared'
import { formatQuantity, numbersEqual } from '@snootsvet/shared'

import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/frontend/components/ui/tooltip'
import type {
  EnrichedAllocationItem,
  StockAllocationStatus,
  StockRowStatus,
} from '@/frontend/lib/stock'
import { cn } from '@/frontend/lib/utils'

/** Allocated/required progress indicator with check/warning icon. */
export function AllocationProgressBadge({
  allocated,
  required,
}: {
  allocated: number
  required: number
}) {
  const matches = numbersEqual(allocated, required)
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-sm',
        matches ? 'text-success-accent' : 'text-destructive-accent'
      )}
    >
      {matches ? <Check className="size-3" /> : <AlertTriangle className="size-3" />}
      {formatQuantity(allocated)}/{formatQuantity(required)} allocated
    </span>
  )
}

/** Local allocation state derived from the user's current batch assignments. */
export type AllocationStatus = 'valid' | 'under-allocated' | 'over-allocated'

export type { EnrichedAllocationItem } from '@/frontend/lib/stock'

/** Returns a short warning label for a batch row status. */
function batchWarningLabel(status?: StockRowStatus): string | null {
  switch (status) {
    case 'expired':
    case 'expired_depleted':
      return 'Expired'
    case 'depleted':
      return 'Depleted'
    case 'unbatched_mixed':
      return 'n/a'
    default:
      return null
  }
}

/** Whether an allocation row has a warning-level batch status. */
function isBatchWarning(status?: StockRowStatus): boolean {
  return batchWarningLabel(status) != null
}

/** Returns a stock-level warning message for a given stock status. */
export function stockWarningMessage(stockStatus: StockAllocationStatus | null): string | null {
  switch (stockStatus) {
    case 'no-stock':
      return 'No stock exists for this product'
    case 'no-valid-stock':
      return 'All batches are expired or depleted'
    case 'insufficient-stock':
      return 'Not enough valid stock to cover this quantity'
    case 'mixed-unbatched':
      return 'Has mixed batched and unbatched stock'
    default:
      return null
  }
}

/** Derives allocation status from the current batch assignments and target quantity. */
export function getCurrentAllocationStatus(
  allocations: BatchAllocationItem[],
  quantity: number | null
): AllocationStatus | null {
  if (allocations.length === 0) return null
  const allocated = allocations.reduce((sum, a) => sum + a.quantity, 0)
  if (numbersEqual(allocated, quantity ?? 0)) return 'valid'
  return allocated > (quantity ?? 0) ? 'over-allocated' : 'under-allocated'
}

interface StockAllocationStatusProps {
  allocations: EnrichedAllocationItem[]
  ready: boolean
  stockStatus: StockAllocationStatus | null
  allocationStatus: AllocationStatus | null
}

/** Compact badge for the checkout table - shows stock allocation summary or warning. */
export function StockAllocationBadge({
  allocations,
  ready,
  allocationStatus,
  stockStatus,
  className,
  ...rest
}: StockAllocationStatusProps & React.ComponentProps<typeof Badge>) {
  const hasRowWarnings = allocations.some((a) => isBatchWarning(a.batchStatus))
  const isHealthy = allocationStatus === 'valid' && stockStatus === 'ok' && !hasRowWarnings
  const label = !ready
    ? 'Loading…'
    : stockStatus === 'no-stock'
      ? 'No stock'
      : allocations.length === 0
        ? 'Unallocated'
        : allocations.length === 1
          ? (allocations[0].batchNumber ?? 'n/a')
          : `${allocations.length} batches`

  const tooltipContent =
    allocations.length > 0
      ? allocations.map((a) => {
          const warning = batchWarningLabel(a.batchStatus)
          const expiry = a.expiryDate
            ? `Exp ${parseISO(a.expiryDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`
            : null
          return (
            <div key={a.batchNumber} className="flex items-center gap-3">
              <span className="font-medium">{a.batchNumber ?? 'No batch number'}</span>
              <span>×{a.quantity}</span>
              {expiry && <span className="text-muted-foreground">{expiry}</span>}
              {warning && a.batchNumber != null && (
                <span className="text-warning-accent">{warning}</span>
              )}
            </div>
          )
        })
      : stockWarningMessage(stockStatus)

  const badge = (
    <Badge
      variant="outline"
      {...rest}
      className={cn(
        !ready
          ? 'text-muted-foreground'
          : isHealthy
            ? 'border-success-border bg-success text-success-accent'
            : allocationStatus === 'valid'
              ? 'border-warning-border bg-warning text-warning-accent'
              : 'border-destructive/40 bg-destructive/10 text-destructive-accent',
        className
      )}
    >
      {ready && allocationStatus === 'valid' && <Check className="size-3" />}
      {ready && allocationStatus !== 'valid' && <AlertTriangle className="size-3" />}
      {label}
    </Badge>
  )

  if (!ready || !tooltipContent) return badge

  return (
    <Tooltip>
      <TooltipTrigger render={badge} />
      <TooltipContent className="text-sm">{tooltipContent}</TooltipContent>
    </Tooltip>
  )
}

/** Inline batch summary badges for the treatment form header row. */
export function StockAllocationBatchSummary({
  allocations,
  ready,
  stockStatus,
}: {
  allocations: EnrichedAllocationItem[]
  ready: boolean
  stockStatus: StockAllocationStatus | null
}) {
  const warning = stockWarningMessage(stockStatus)
  const isDestructive = stockStatus === 'no-stock'

  if (!ready) return <span className="text-muted-foreground">Loading…</span>

  if (allocations.length > 0) {
    return (
      <div className="flex min-w-0 flex-wrap items-center gap-1">
        {allocations
          .filter((a) => a.quantity > 0)
          .map((a) => {
            const hasWarning = isBatchWarning(a.batchStatus)
            return (
              <Badge
                key={a.batchNumber}
                variant="outline"
                className={cn(
                  'gap-1 font-normal',
                  hasWarning
                    ? 'border-warning-border bg-warning text-warning-accent'
                    : 'border-success-border bg-success text-success-accent'
                )}
              >
                {hasWarning && <AlertTriangle className="size-3" />}
                {a.batchNumber ?? 'No batch number'} × {a.quantity}
              </Badge>
            )
          })}
      </div>
    )
  }

  if (warning) {
    return (
      <span className={cn('', isDestructive ? 'text-destructive-accent' : 'text-warning-accent')}>
        {warning}
      </span>
    )
  }

  return <span className="text-warning-accent">Allocation mismatch</span>
}

/** Resets the batch allocation to the FEFO suggestion. */
export function StockAllocationResetButton({ onReset }: { onReset: () => void }) {
  return (
    <Button type="button" variant="ghost" onClick={onReset}>
      <RotateCcw className="size-3.5" />
      Reset
    </Button>
  )
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Validates a stock allocations array against a target quantity.
 * Intended for use inside a Zod `.superRefine()` on a parent object,
 * paired with a `.transform()` step that strips zero-quantity rows.
 *
 * Zero-quantity rows are tolerated when the non-zero rows already sum to
 * the target quantity - the user may be mid-edit on a mixed allocation
 * and shouldn't be penalised for rows they haven't filled in yet. When the
 * sum doesn't match, zero-quantity rows are flagged individually alongside
 * the overall mismatch error.
 */
export function validateStockAllocations(
  allocations: { batchNumber: string | null; quantity: number }[],
  quantity: number,
  ctx: z.RefinementCtx
) {
  if (allocations.length === 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'Please allocate batches',
      path: ['allocations'],
    })
    return
  }

  const nonZeroSum = allocations.reduce((sum, a) => sum + (a.quantity > 0 ? a.quantity : 0), 0)

  if (numbersEqual(nonZeroSum, quantity)) return

  for (let i = 0; i < allocations.length; i++) {
    if (allocations[i].quantity === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Quantity must be greater than 0',
        path: ['allocations', i, 'quantity'],
      })
    }
  }

  ctx.addIssue({
    code: 'custom',
    message: 'Total stock allocations must match the dispensed quantity',
    path: ['allocations'],
  })
}
